import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { communityApi } from '../lib/api';
import { Users, TrendingUp, MessageSquare, UserPlus, ArrowRight, Activity, Dumbbell, ChevronRight, Flame, Heart, Send, Image, X, Trash2, Loader2, Trophy, Coins } from 'lucide-react';

const ACTIVITY_LABELS = {
  post: 'shared a post',
  training_log: 'logged a workout',
  milestone: 'hit a milestone',
  meal_plan_share: 'shared a meal plan',
  follow: 'started following',
  article_bookmark: 'bookmarked an article',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ========== COMMENTS SECTION ==========
function CommentsSection({ activityId, commentsCount, token, user }) {
  const [comments, setComments] = useState([]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [count, setCount] = useState(commentsCount || 0);

  async function loadComments() {
    if (!open) {
      setOpen(true);
      setLoading(true);
      try {
        const res = await communityApi.getComments(activityId);
        setComments(res.data.comments || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    } else {
      setOpen(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim() || !token) return;
    setPosting(true);
    try {
      const res = await communityApi.addComment(activityId, text.trim(), token);
      setComments(prev => [...prev, res.data]);
      setCount(prev => prev + 1);
      setText('');
    } catch (e) { console.error(e); }
    setPosting(false);
  }

  async function handleDelete(commentId) {
    try {
      await communityApi.deleteComment(commentId, token);
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
      setCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error(e); }
  }

  return (
    <div className="mt-3 pt-3 border-t border-zinc-800/60">
      <button onClick={loadComments} className="flex items-center gap-1.5 text-zinc-500 text-xs hover:text-zinc-300 transition-colors" data-testid={`toggle-comments-${activityId}`}>
        <MessageSquare className="w-3.5 h-3.5" />
        {count > 0 ? `${count} comment${count !== 1 ? 's' : ''}` : 'Comment'}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`comments-section-${activityId}`}>
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-600 text-xs"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
          ) : (
            <>
              {comments.map(c => (
                <div key={c.comment_id} className="flex gap-2 group" data-testid={`comment-${c.comment_id}`}>
                  <Link to={`/community/${c.username}`} className="flex-shrink-0">
                    <div className="w-7 h-7 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-500 text-[10px] font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {c.avatar ? <img src={c.avatar} alt="" className="w-full h-full object-cover" /> : c.display_name?.charAt(0) || '?'}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0 bg-zinc-800/40 px-3 py-2 border border-zinc-800/60">
                    <div className="flex items-center gap-2">
                      <Link to={`/community/${c.username}`} className="text-white text-xs font-medium hover:text-[#00F0FF] transition-colors">
                        {c.display_name}
                      </Link>
                      <span className="text-zinc-600 text-[10px]">{timeAgo(c.created_at)}</span>
                      {user && c.user_id === user.user_id && (
                        <button onClick={() => handleDelete(c.comment_id)} className="ml-auto opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all" data-testid={`delete-comment-${c.comment_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <p className="text-zinc-400 text-xs mt-0.5 leading-relaxed">{c.content}</p>
                  </div>
                </div>
              ))}

              {user && token && (
                <form onSubmit={handleSubmit} className="flex gap-2" data-testid={`comment-form-${activityId}`}>
                  <input
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="Write a comment..."
                    className="flex-1 bg-zinc-800/40 border border-zinc-700 text-white text-xs px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
                    maxLength={1000}
                    data-testid={`comment-input-${activityId}`}
                  />
                  <button type="submit" disabled={posting || !text.trim()} className="px-3 py-2 bg-[#E63946] text-white disabled:opacity-40 hover:bg-[#E63946]/80 transition-colors" data-testid={`comment-submit-${activityId}`}>
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ========== ACTIVITY CARD ==========
function ActivityCard({ activity, token, user, onLike }) {
  const [liked, setLiked] = useState(activity.liked_by_me || false);
  const [likesCount, setLikesCount] = useState(activity.likes_count || 0);
  const [liking, setLiking] = useState(false);

  async function handleLike() {
    if (!token || liking) return;
    setLiking(true);
    try {
      const res = await communityApi.toggleLike(activity.activity_id, token);
      setLiked(res.data.status === 'liked');
      setLikesCount(res.data.likes_count);
      if (onLike) onLike(activity.activity_id, res.data);
    } catch (e) { console.error(e); }
    setLiking(false);
  }

  const label = ACTIVITY_LABELS[activity.type] || 'posted';

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 p-5 hover:border-zinc-700 transition-colors" data-testid={`activity-card-${activity.activity_id || activity.type}`}>
      <div className="flex items-start gap-3">
        <Link to={`/community/${activity.username}`} className="flex-shrink-0">
          <div className="w-10 h-10 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-sm font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            {activity.avatar ? <img src={activity.avatar} alt="" className="w-full h-full object-cover" /> : activity.display_name?.charAt(0) || '?'}
          </div>
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/community/${activity.username}`} className="text-white text-sm font-medium hover:text-[#00F0FF] transition-colors">
              {activity.display_name || activity.username}
            </Link>
            <span className="text-zinc-500 text-xs">{label}</span>
            {activity.target_username && (
              <Link to={`/community/${activity.target_username}`} className="text-[#00F0FF] text-xs hover:underline">@{activity.target_username}</Link>
            )}
            <span className="text-zinc-600 text-xs ml-auto flex-shrink-0">{timeAgo(activity.created_at)}</span>
          </div>

          {activity.title && (
            <h4 className="text-white text-sm mt-2 font-medium" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1rem' }}>{activity.title}</h4>
          )}
          {activity.content && (
            <p className="text-zinc-400 text-sm mt-1 leading-relaxed">{activity.content}</p>
          )}

          {/* Images */}
          {activity.images && activity.images.length > 0 && (
            // eslint-disable-next-line no-nested-ternary -- readable tier picker
            <div className={`mt-3 grid gap-2 ${activity.images.length === 1 ? 'grid-cols-1' : activity.images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`} data-testid="activity-images">
              {activity.images.map((img, i) => (
                <div key={img} className="aspect-square bg-zinc-800 border border-zinc-700 overflow-hidden">
                  <img src={communityApi.getImageUrl(img)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
                </div>
              ))}
            </div>
          )}

          {activity.metadata && Object.keys(activity.metadata).length > 0 && (
            <div className="flex gap-3 mt-3 flex-wrap">
              {Object.entries(activity.metadata).slice(0, 4).map(([key, val]) => (
                <span key={key} className="px-2 py-1 text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700">
                  {key}: {val}
                </span>
              ))}
            </div>
          )}

          {/* Like + Comment bar */}
          {activity.activity_id && activity.type !== 'follow' && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-800/60">
              <button
                onClick={handleLike}
                disabled={!token}
                className={`flex items-center gap-1.5 text-xs transition-colors ${
                  liked ? 'text-[#E63946]' : 'text-zinc-500 hover:text-[#E63946]'
                } ${!token ? 'opacity-40 cursor-not-allowed' : ''}`}
                data-testid={`like-btn-${activity.activity_id}`}
              >
                <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-[#E63946]' : ''}`} />
                {likesCount > 0 && likesCount}
              </button>

              <CommentsSection
                activityId={activity.activity_id}
                commentsCount={activity.comments_count || 0}
                token={token}
                user={user}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ========== PROFILE CARD ==========
function ProfileCard({ profile, onFollow, followingSet, isLoggedIn }) {
  const isFollowing = followingSet.has(profile.user_id);

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 p-4 hover:border-zinc-700 transition-colors" data-testid={`profile-card-${profile.username}`}>
      <div className="flex items-center gap-3">
        <Link to={`/community/${profile.username}`} className="flex-shrink-0">
          <div className="w-12 h-12 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-lg font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            {profile.avatar ? <img src={profile.avatar} alt="" className="w-full h-full object-cover" /> : profile.display_name?.charAt(0) || '?'}
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/community/${profile.username}`} className="text-white text-sm font-medium hover:text-[#00F0FF] transition-colors block truncate">{profile.display_name}</Link>
          <span className="text-zinc-500 text-xs">@{profile.username}</span>
          {profile.bio && <p className="text-zinc-400 text-xs mt-1 line-clamp-1">{profile.bio}</p>}
        </div>
        {isLoggedIn && (
          <button
            onClick={() => onFollow(profile.username, profile.user_id)}
            className={`flex-shrink-0 px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
              isFollowing ? 'border border-zinc-700 text-zinc-400 hover:border-red-500/50 hover:text-red-400' : 'bg-[#E63946] text-white hover:bg-[#E63946]/80'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid={`follow-btn-${profile.username}`}
          >
            {isFollowing ? 'Following' : 'Follow'}
          </button>
        )}
      </div>
      <div className="flex gap-4 mt-3 pt-3 border-t border-zinc-800/60">
        <span className="text-zinc-500 text-xs"><strong className="text-zinc-300">{profile.followers_count || 0}</strong> followers</span>
        <span className="text-zinc-500 text-xs"><strong className="text-zinc-300">{profile.posts_count || 0}</strong> posts</span>
      </div>
    </div>
  );
}

// ========== CREATE POST ==========
function CreatePost({ token, onPosted }) {
  const [content, setContent] = useState('');
  const [type, setType] = useState('post');
  const [title, setTitle] = useState('');
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  async function handleImageSelect(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (images.length + files.length > 3) {
      alert('Maximum 3 images per post');
      return;
    }

    setUploading(true);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { alert(`${file.name} exceeds 5MB limit`); continue; }
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) { alert(`${file.name}: Invalid type`); continue; }

      try {
        const res = await communityApi.uploadImage(file, token);
        setImages(prev => [...prev, { path: res.data.path, preview: URL.createObjectURL(file) }]);
      } catch (err) {
        console.error('Upload error:', err);
        alert('Upload failed. Please try again.');
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  function removeImage(idx) {
    setImages(prev => {
      const next = [...prev];
      URL.revokeObjectURL(next[idx].preview);
      next.splice(idx, 1);
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      await communityApi.postActivity({
        type,
        title,
        content,
        images: images.map(img => img.path),
      }, token);
      setContent('');
      setTitle('');
      setImages([]);
      setExpanded(false);
      onPosted();
    } catch (err) { console.error('Post error:', err); }
    setPosting(false);
  }

  const types = [
    { value: 'post', label: 'Post' },
    { value: 'training_log', label: 'Training Log' },
    { value: 'milestone', label: 'Milestone' },
    { value: 'meal_plan_share', label: 'Meal Plan' },
  ];

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900/60 border border-zinc-800 p-4" data-testid="create-post-form">
      {!expanded ? (
        <button type="button" onClick={() => setExpanded(true)} className="w-full text-left text-zinc-500 text-sm py-2 px-3 bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600 transition-colors" data-testid="create-post-trigger">
          Share a training update, milestone, or thought...
        </button>
      ) : (
        <>
          <div className="flex gap-2 mb-3 flex-wrap">
            {types.map(t => (
              <button key={t.value} type="button" onClick={() => setType(t.value)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors ${type === t.value ? 'bg-[#E63946] text-white' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'}`}
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >{t.label}</button>
            ))}
          </div>
          {(type === 'training_log' || type === 'milestone') && (
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder={type === 'training_log' ? 'Workout name (e.g., Morning HYROX Sim)' : 'Milestone (e.g., First Sub-90 HYROX!)'}
              className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 mb-3 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              data-testid="create-post-title"
            />
          )}
          <textarea value={content} onChange={e => setContent(e.target.value)}
            placeholder="What's on your mind?" rows={3}
            className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 mb-3 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 resize-none"
            data-testid="create-post-content"
          />

          {/* Image previews */}
          {images.length > 0 && (
            <div className="flex gap-2 mb-3 flex-wrap" data-testid="image-previews">
              {images.map((img, i) => (
                <div key={img.preview} className="relative w-20 h-20 bg-zinc-800 border border-zinc-700 overflow-hidden group">
                  <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/70 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${i}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleImageSelect} className="hidden" data-testid="image-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || images.length >= 3}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-400 border border-zinc-700 hover:border-zinc-500 hover:text-zinc-200 transition-colors disabled:opacity-40"
                data-testid="add-image-btn"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Image className="w-3.5 h-3.5" />}
                {uploading ? 'Uploading...' : `Photo ${images.length}/3`}
              </button>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setExpanded(false); setImages([]); }} className="px-4 py-2 text-xs text-zinc-400 hover:text-white transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Cancel</button>
              <button type="submit" disabled={posting || !content.trim()}
                className="px-5 py-2 bg-[#E63946] text-white text-xs uppercase tracking-wider disabled:opacity-40 hover:bg-[#E63946]/80 transition-colors"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="create-post-submit"
              >{posting ? 'Posting...' : 'Post'}</button>
            </div>
          </div>
        </>
      )}
    </form>
  );
}

// ========== MAIN PAGE ==========
export default function CommunityPage() {
  const { user, token } = useAuth();
  const [discover, setDiscover] = useState(null);
  const [publicFeed, setPublicFeed] = useState([]);
  const [myFeed, setMyFeed] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [followingSet, setFollowingSet] = useState(new Set());
  const [activeTab, setActiveTab] = useState(user ? 'feed' : 'discover');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const discoverRes = await communityApi.discover();
      setDiscover(discoverRes.data);
      setPublicFeed(discoverRes.data.recent_activity || []);

      if (user && token) {
        const [feedRes, suggestRes] = await Promise.all([
          communityApi.getFeed(token).catch(() => ({ data: { feed: [] } })),
          communityApi.getSuggestions(token).catch(() => ({ data: { suggestions: [] } })),
        ]);
        setMyFeed(feedRes.data.feed || []);
        setSuggestions(suggestRes.data.suggestions || []);
      }
    } catch (e) { console.error('Community load error:', e); }
    setLoading(false);
  }, [user, token]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleFollow(username, userId) {
    if (!token) return;
    const isFollowing = followingSet.has(userId);
    try {
      if (isFollowing) {
        await communityApi.unfollow(username, token);
        setFollowingSet(prev => { const n = new Set(prev); n.delete(userId); return n; });
      } else {
        await communityApi.follow(username, token);
        setFollowingSet(prev => new Set(prev).add(userId));
      }
    } catch (e) { console.error('Follow error:', e); }
  }

  const feed = activeTab === 'feed' ? myFeed : publicFeed;
  const profileList = activeTab === 'discover' ? (discover?.top_profiles || []) : suggestions;

  return (
    <main className="pt-20 min-h-screen" data-testid="community-page">
      <div className="container-ninja section-spacing">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="text-ninja-label text-[#00F0FF] mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Community</p>
            <h1 className="text-white text-4xl sm:text-5xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="community-title">The Dojo</h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-md">Connect with athletes, share training logs, and follow the community's progress.</p>
          </div>
          {discover?.stats && (
            <div className="flex gap-5">
              <div className="text-center">
                <span className="text-white text-xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{discover.stats.total_members}</span>
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Members</span>
              </div>
              <div className="text-center">
                <span className="text-white text-xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{discover.stats.total_posts}</span>
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Posts</span>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-zinc-800" data-testid="community-tabs">
          {user && (
            <button onClick={() => setActiveTab('feed')}
              className={`px-5 py-3 text-xs uppercase tracking-wider transition-colors ${activeTab === 'feed' ? 'text-[#E63946] border-b-2 border-[#E63946]' : 'text-zinc-500 hover:text-zinc-300'}`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="tab-feed">My Feed</button>
          )}
          <button onClick={() => setActiveTab('discover')}
            className={`px-5 py-3 text-xs uppercase tracking-wider transition-colors ${activeTab === 'discover' ? 'text-[#E63946] border-b-2 border-[#E63946]' : 'text-zinc-500 hover:text-zinc-300'}`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="tab-discover">Discover</button>
          <button onClick={() => setActiveTab('members')}
            className={`px-5 py-3 text-xs uppercase tracking-wider transition-colors ${activeTab === 'members' ? 'text-[#E63946] border-b-2 border-[#E63946]' : 'text-zinc-500 hover:text-zinc-300'}`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="tab-members">Members</button>
          <Link to="/community/challenges"
            className="px-5 py-3 text-xs uppercase tracking-wider text-zinc-500 hover:text-[#FFD700] transition-colors flex items-center gap-1.5"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="tab-challenges">
            <Trophy className="w-3.5 h-3.5" /> Challenges
          </Link>
          <Link to="/community/rewards"
            className="px-5 py-3 text-xs uppercase tracking-wider text-zinc-500 hover:text-[#00F0FF] transition-colors flex items-center gap-1.5"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="tab-rewards">
            <Coins className="w-3.5 h-3.5" /> Rewards
          </Link>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-zinc-900/50 animate-pulse border border-zinc-800" />)}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Feed */}
            <div className="lg:col-span-2 space-y-4">
              {user && activeTab === 'feed' && <CreatePost token={token} onPosted={loadData} />}

              {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
              {activeTab === 'members' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(discover?.top_profiles || []).map(p => (
                    <ProfileCard key={p.username} profile={p} onFollow={handleFollow} followingSet={followingSet} isLoggedIn={!!user} />
                  ))}
                  {(discover?.top_profiles || []).length === 0 && (
                    <p className="text-zinc-500 text-sm col-span-2 text-center py-12">No members yet. Be the first to join!</p>
                  )}
                </div>
              ) : feed.length > 0 ? (
                feed.map((a, i) => <ActivityCard key={a.activity_id || `${a.created_at}-${i}`} activity={a} token={token} user={user} />)
              ) : (
                <div className="text-center py-16 bg-zinc-900/40 border border-zinc-800">
                  <Activity className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400 text-sm mb-2">{activeTab === 'feed' ? 'Your feed is empty. Follow some athletes!' : 'No activity yet. Be the first to post!'}</p>
                  {!user && (
                    <Link to="/auth" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[#E63946] text-white text-xs uppercase tracking-wider hover:bg-[#E63946]/80 transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="community-join-btn">
                      Join the Community <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              <div className="bg-zinc-900/40 border border-zinc-800 p-5">
                <h3 className="text-white text-sm uppercase tracking-wider mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{user ? 'Suggested Athletes' : 'Top Members'}</h3>
                <div className="space-y-3">
                  {(user ? suggestions : (discover?.top_profiles || []).slice(0, 5)).map(p => (
                    <div key={p.username} className="flex items-center gap-3">
                      <Link to={`/community/${p.username}`} className="flex-shrink-0">
                        <div className="w-9 h-9 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-xs font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                          {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" /> : p.display_name?.charAt(0) || '?'}
                        </div>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/community/${p.username}`} className="text-white text-xs font-medium hover:text-[#00F0FF] transition-colors block truncate">{p.display_name}</Link>
                        <span className="text-zinc-600 text-[10px]">@{p.username}</span>
                      </div>
                      {user && (
                        <button onClick={() => handleFollow(p.username, p.user_id)}
                          className={`text-[10px] uppercase tracking-wider px-2 py-1 transition-colors ${followingSet.has(p.user_id) ? 'text-zinc-500 border border-zinc-700' : 'text-[#E63946] border border-[#E63946]/40 hover:bg-[#E63946]/10'}`}
                          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                        >{followingSet.has(p.user_id) ? 'Following' : 'Follow'}</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {!user && (
                <div className="bg-gradient-to-b from-[#E63946]/10 to-transparent border border-[#E63946]/30 p-5 text-center">
                  <h3 className="text-white text-lg mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Join the Dojo</h3>
                  <p className="text-zinc-400 text-xs mb-4">Create your profile, follow athletes, and share your training journey.</p>
                  <Link to="/auth" className="inline-block w-full py-2.5 bg-[#E63946] text-white text-xs uppercase tracking-wider text-center hover:bg-[#E63946]/80 transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="sidebar-join-btn">Sign Up Free</Link>
                </div>
              )}

              {user && (
                <Link to="/community/me" className="block bg-zinc-900/40 border border-zinc-800 p-4 hover:border-[#00F0FF]/30 transition-colors" data-testid="my-profile-link">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[#00F0FF] text-sm font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{user.name?.charAt(0) || user.first_name?.charAt(0) || '?'}</div>
                    <div>
                      <span className="text-white text-sm block">{user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Your Profile'}</span>
                      <span className="text-zinc-500 text-xs">View & edit your profile</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 ml-auto" />
                  </div>
                </Link>
              )}
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}
